import React from 'react';
import useHideMobileNav from '../utils/useHideMobileNav';

/**
 * Soft-warning modal shown when the detector suspects a user may be
 * tracking multiple physical locations under one paid store account.
 *
 * Props:
 *   onContinue()          — dismiss without action
 *   onAddStore()          — routes through window.startAddPaidStore()
 *   onDontShowAgain()     — suppresses future warnings for this fingerprint
 *   templateNames         — string[] — the suspect template names
 *   score                 — number
 */
const SubscriptionMisuseModal = ({
  onContinue,
  onAddStore,
  onDontShowAgain,
  templateNames = [],
  score = 0,
}) => {
  useHideMobileNav(true);

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.48)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  };

  const dialogStyle = {
    background: '#fff',
    borderRadius: 16,
    padding: '1.75rem 1.5rem 1.5rem',
    maxWidth: 460,
    width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
    fontFamily: "'Open Sans', system-ui, sans-serif",
  };

  const titleStyle = {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '1.1rem',
    color: '#3D2518',
    margin: '0 0 0.85rem',
    lineHeight: 1.3,
  };

  const bodyStyle = {
    fontSize: '0.9rem',
    color: '#5A4A42',
    lineHeight: 1.6,
    margin: '0 0 1.1rem',
  };

  const templateListStyle = {
    background: '#FAF6F2',
    border: '1px solid #E8DDD0',
    borderRadius: 8,
    padding: '0.6rem 0.85rem',
    margin: '0.6rem 0 0',
    fontSize: '0.85rem',
    color: '#5A4A42',
  };

  const btnRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1.25rem',
  };

  const primaryBtnStyle = {
    padding: '0.65rem 1.1rem',
    background: '#5A4A42',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
  };

  const secondaryBtnStyle = {
    padding: '0.6rem 1.1rem',
    background: '#F5E2D0',
    color: '#5A4A42',
    border: '1px solid #D4C4B0',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: '0.88rem',
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
  };

  const ghostBtnStyle = {
    padding: '0.55rem 1rem',
    background: 'transparent',
    color: '#8B7B73',
    border: '1px solid #D4C4B0',
    borderRadius: 10,
    fontWeight: 500,
    fontSize: '0.82rem',
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
  };

  const iconStyle = {
    fontSize: '2rem',
    marginBottom: '0.6rem',
    display: 'block',
    textAlign: 'center',
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="misuse-modal-title">
      <div style={dialogStyle}>
        <span style={iconStyle}>🏪</span>
        <h2 id="misuse-modal-title" style={titleStyle}>
          Possible multiple-store setup detected
        </h2>
        <p style={bodyStyle}>
          It looks like you may be using separate count templates to track different physical
          locations — which is what a second store account is designed for.
        </p>
        <p style={bodyStyle}>
          Each store account gets its own inventory, reports, and team. Adding a second store keeps
          things organized and ensures accurate reporting per location.
        </p>
        {templateNames.length > 0 && (
          <div style={templateListStyle}>
            <strong style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8B7B73' }}>
              Templates that triggered this check:
            </strong>
            <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
              {templateNames.map((name, i) => (
                <li key={i} style={{ marginBottom: '0.15rem' }}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        <div style={btnRowStyle}>
          <button
            type="button"
            style={secondaryBtnStyle}
            onClick={onContinue}
          >
            Continue
          </button>
          <button
            type="button"
            style={primaryBtnStyle}
            onClick={onAddStore}
          >
            Add Store
          </button>
          <button
            type="button"
            style={ghostBtnStyle}
            onClick={onDontShowAgain}
          >
            Don't show again for these templates
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionMisuseModal;
